import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Search, FileDown, Plus, Pencil, Trash2, RefreshCw, Database, FolderOpen, Columns, Eye, EyeOff, CalendarDays, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

const PROJECT_FIELDS = [
  { key: 'job_id', label: 'Atlas Job ID' },
  { key: 'po_number', label: 'PO/WO Number' },
  { key: 'program_number', label: 'Program Number' },
  { key: 'main_title', label: 'Main Title' },
  { key: 'episode_title', label: 'Episode Title' },
  { key: 'season', label: 'Season #' },
  { key: 'episode', label: 'Episode #' },
  { key: 'client', label: 'Client' },
  { key: 'office', label: 'Office' },
  { key: 'task', label: 'Task' },
  { key: 'language', label: 'Language' },
  { key: 'task_status', label: 'Task Status' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'scheduler', label: 'Scheduler' },
  { key: 'resource', label: 'Resource' },
  { key: 'qty', label: 'Duration (QTY)' },
];

const DEFAULT_VISIBLE = ['main_title', 'episode_title', 'task', 'language', 'task_status', 'due_date', 'qty', 'resource'];

const ProjectStats = ({ projects }) => {
  const stats = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    let inProgress = 0, completed = 0, thisWeek = 0, thisMonth = 0;
    for (const p of projects) {
      if (p.task_status === 'in progress') inProgress++;
      else completed++;

      const d = p.due_date ? new Date(p.due_date) : null;
      const c = p.created_at ? new Date(p.created_at) : null;
      const ref = d || c;
      if (ref && !isNaN(ref)) {
        if (ref >= startOfMonth) thisMonth++;
        if (ref >= startOfWeek) thisWeek++;
      }
    }
    return { inProgress, completed, thisWeek, thisMonth };
  }, [projects]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card className="border-l-4 border-l-blue-500">
        <CardContent className="pt-4 pb-3 text-center">
          <Loader2 className="w-5 h-5 mx-auto text-blue-500 mb-1" />
          <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
          <p className="text-xs text-muted-foreground">In Progress</p>
        </CardContent>
      </Card>
      <Card className="border-l-4 border-l-green-500">
        <CardContent className="pt-4 pb-3 text-center">
          <CheckCircle2 className="w-5 h-5 mx-auto text-green-500 mb-1" />
          <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          <p className="text-xs text-muted-foreground">Completed</p>
        </CardContent>
      </Card>
      <Card className="border-l-4 border-l-violet-500">
        <CardContent className="pt-4 pb-3 text-center">
          <CalendarDays className="w-5 h-5 mx-auto text-violet-500 mb-1" />
          <p className="text-2xl font-bold text-violet-600">{stats.thisMonth}</p>
          <p className="text-xs text-muted-foreground">This Month</p>
        </CardContent>
      </Card>
      <Card className="border-l-4 border-l-amber-500">
        <CardContent className="pt-4 pb-3 text-center">
          <CalendarDays className="w-5 h-5 mx-auto text-amber-500 mb-1" />
          <p className="text-2xl font-bold text-amber-600">{stats.thisWeek}</p>
          <p className="text-xs text-muted-foreground">This Week</p>
        </CardContent>
      </Card>
    </div>
  );
};

const OwnerClientProjects = () => {
  const { user } = useOutletContext();
  const isOwner = user?.role === 'owner';
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [rematching, setRematching] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [visibleCols, setVisibleCols] = useState(() => {
    const saved = localStorage.getItem('graphikpro-project-cols');
    return saved ? JSON.parse(saved) : DEFAULT_VISIBLE;
  });

  const toggleCol = (key) => {
    setVisibleCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      localStorage.setItem('graphikpro-project-cols', JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => { loadProjects(); }, []);

  const loadProjects = async () => {
    try {
      const res = await api.get('/client-projects');
      setProjects(res.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = projects;
    // Month filter by due_date
    if (selectedMonth !== 'all') {
      const [fy, fm] = selectedMonth.split('-').map(Number);
      list = list.filter(p => {
        const d = p.due_date || '';
        if (!d) return false;
        try {
          const clean = d.replace(/\s*due\s+in\s+\d+\s+days?\s*/i, '').trim();
          if (clean.includes('/')) {
            const parts = clean.split(/[/,\s]+/);
            return parts.length >= 3 && parseInt(parts[2]) === fy && parseInt(parts[1]) === fm;
          }
          const parsed = new Date(clean);
          return parsed.getFullYear() === fy && (parsed.getMonth() + 1) === fm;
        } catch { return false; }
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p => Object.values(p).some(v => String(v).toLowerCase().includes(q)));
    }
    return list;
  }, [projects, searchQuery, selectedMonth]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/owner/upload-projects-excel', formData);
      toast.success(`${res.data.message}. QTY matched: ${res.data.qty_matched}`);
      loadProjects();
      setShowImport(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRematchQty = async () => {
    setRematching(true);
    try {
      const res = await api.post('/owner/client-projects/rematch-qty');
      toast.success(res.data.message);
      loadProjects();
    } catch (error) {
      toast.error('Re-match failed');
    } finally {
      setRematching(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editProject) {
        await api.put(`/owner/client-projects/${editProject.project_id}`, formData);
        toast.success('Project updated');
      } else {
        await api.post('/owner/client-projects', formData);
        toast.success('Project added');
      }
      setShowAdd(false);
      setEditProject(null);
      setFormData({});
      loadProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (projectId) => {
    if (!confirm('Delete this project?')) return;
    try {
      await api.delete(`/owner/client-projects/${projectId}`);
      toast.success('Project deleted');
      loadProjects();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleDeleteAll = async () => {
    if (!confirm('Delete ALL client projects? This cannot be undone.')) return;
    try {
      await api.delete('/owner/client-projects');
      toast.success('All projects deleted');
      loadProjects();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const openEdit = (project) => {
    setEditProject(project);
    setFormData({ ...project });
    setShowAdd(true);
  };

  const openAdd = () => {
    setEditProject(null);
    setFormData({});
    setShowAdd(true);
  };

  const statusBadge = (status) => {
    if (status === 'completed') return <Badge className="bg-green-600 text-white text-xs">Completed</Badge>;
    return <Badge className="bg-blue-500 text-white text-xs">In Progress</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold accent-gradient" data-testid="client-projects-title">Client Projects</h1>
            <p className="text-muted-foreground">Project database — {projects.length} total records</p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Select value={selectedMonth} onValueChange={(v) => { setSelectedMonth(v); setPage(1); }}>
              <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {Array.from({ length: 12 }, (_, i) => { const d = new Date(); d.setMonth(d.getMonth() - i); const v = d.toISOString().slice(0, 7); return <SelectItem key={v} value={v}>{d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}</SelectItem>; })}
              </SelectContent>
            </Select>
            {isOwner && (
              <>
                <Button variant="outline" size="sm" onClick={handleRematchQty} disabled={rematching} data-testid="rematch-qty-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${rematching ? 'animate-spin' : ''}`} />
                  {rematching ? 'Matching...' : 'Re-match QTY'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-projects-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Import Excel
                </Button>
                <Button size="sm" onClick={openAdd} data-testid="add-project-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Project
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Search + Pagination controls */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              placeholder="Search projects..."
              className="pl-10"
              data-testid="project-search-input"
            />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="columns-toggle-btn">
                <Columns className="w-4 h-4 mr-2" />
                Columns ({visibleCols.length}/{PROJECT_FIELDS.length})
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 max-h-80 overflow-y-auto" align="end">
              <div className="space-y-1">
                <p className="text-sm font-semibold mb-2">Visible Columns</p>
                {PROJECT_FIELDS.map(f => (
                  <label key={f.key} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted cursor-pointer text-sm">
                    <Checkbox
                      checked={visibleCols.includes(f.key)}
                      onCheckedChange={() => toggleCol(f.key)}
                    />
                    {f.label}
                  </label>
                ))}
                <div className="border-t pt-2 mt-2 flex gap-2">
                  <Button variant="ghost" size="sm" className="text-xs flex-1" onClick={() => {
                    const all = PROJECT_FIELDS.map(f => f.key);
                    setVisibleCols(all);
                    localStorage.setItem('graphikpro-project-cols', JSON.stringify(all));
                  }}>Show All</Button>
                  <Button variant="ghost" size="sm" className="text-xs flex-1" onClick={() => {
                    setVisibleCols(DEFAULT_VISIBLE);
                    localStorage.setItem('graphikpro-project-cols', JSON.stringify(DEFAULT_VISIBLE));
                  }}>Reset</Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Show</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(s => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <span>of {filtered.length} records</span>
          </div>
          {isOwner && projects.length > 0 && (
            <Button variant="ghost" size="sm" className="text-destructive" onClick={handleDeleteAll}>
              <Trash2 className="w-4 h-4 mr-1" /> Clear All
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        {projects.length > 0 && <ProjectStats projects={projects} />}

        {/* Table */}
        {projects.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Database className="w-16 h-16 mx-auto text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Client Projects Yet</h3>
              <p className="text-muted-foreground mb-4">Import an Excel file to populate the project database</p>
              {isOwner && (
                <Button onClick={() => setShowImport(true)}>
                  <Upload className="w-4 h-4 mr-2" /> Import Excel
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 text-center">#</TableHead>
                      {PROJECT_FIELDS.filter(f => visibleCols.includes(f.key)).map(f => (
                        <TableHead key={f.key} className="whitespace-nowrap text-center">{f.label}</TableHead>
                      ))}
                      {isOwner && <TableHead className="w-20 text-center">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginated.map((project, idx) => (
                      <TableRow key={project.project_id}>
                        <TableCell className="text-center text-muted-foreground text-xs">
                          {(page - 1) * pageSize + idx + 1}
                        </TableCell>
                        {PROJECT_FIELDS.filter(f => visibleCols.includes(f.key)).map(f => (
                          <TableCell key={f.key} className="whitespace-nowrap text-sm text-center">
                            {f.key === 'task_status' ? statusBadge(project[f.key]) :
                             f.key === 'qty' ? (project.qty > 0 ? project.qty : <span className="text-muted-foreground">—</span>) :
                             project[f.key] || <span className="text-muted-foreground">—</span>}
                          </TableCell>
                        ))}
                        {isOwner && (
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEdit(project)} className="h-7 w-7 p-0">
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(project.project_id)} className="h-7 w-7 p-0 text-destructive">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Client Projects</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload an Excel or CSV file with columns: Atlas Job ID, PO/WO Number, Program Number, Main Title, Episode Title, Season #, Episode #, Client, Office, Task, Language, Task Status, Due Date, Scheduler, Resource.
            </p>
            <p className="text-sm text-muted-foreground">
              <strong>Duration (QTY)</strong> will be auto-matched from Client Billings using PO Number + Job ID.
            </p>
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <FolderOpen className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
              <input
                type="file"
                id="project-excel"
                className="hidden"
                accept=".xlsx,.xls,.csv"
                onChange={handleImportExcel}
                disabled={uploading}
              />
              <Button variant="outline" onClick={() => document.getElementById('project-excel').click()} disabled={uploading}>
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Importing...' : 'Choose File'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">Supported: .xlsx, .xls, .csv</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={(open) => { setShowAdd(open); if (!open) { setEditProject(null); setFormData({}); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editProject ? 'Edit Project' : 'Add Project'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {PROJECT_FIELDS.map(f => (
              <div key={f.key} className="space-y-1">
                <Label className="text-xs">{f.label}</Label>
                {f.key === 'task_status' ? (
                  <Select value={formData.task_status || 'in progress'} onValueChange={(v) => setFormData(prev => ({ ...prev, task_status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={formData[f.key] || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.label}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleSave}>{editProject ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default OwnerClientProjects;
